import type { ReactiveController, ReactiveControllerHost } from "lit";

export type FlipRect = { left: number; width: number };

export type FlipTransform = { translateX: number; scaleX: number };

// FLIP: given an element's previous and next geometry, return the transform that
// visually places it back at its previous position/size. Animating from this
// transform to identity makes a layout change look like a smooth move/resize.
export function computeFlipTransform(
  prev: FlipRect,
  next: FlipRect,
): FlipTransform {
  const scaleX = next.width === 0 ? 1 : prev.width / next.width;
  const translateX = prev.left - next.left;
  return { translateX, scaleX };
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const FLIP_DURATION_MS = 200;
const FLIP_EASING = "ease-out";
// Below these deltas a change is imperceptible, so we skip animating to avoid jitter.
const FLIP_MIN_TRANSLATE_PX = 0.5;
const FLIP_MIN_SCALE_DELTA = 0.01;

type FlipHost = ReactiveControllerHost & { renderRoot: ParentNode };

export type TrackedCell = FlipRect & { el: HTMLElement };

export type FlipDecision = {
  el: HTMLElement;
  translateX: number;
  scaleX: number;
};

// Pure decision core of the FLIP pass: given the previous and next geometry
// snapshots, return the animations to play. Only cells present in BOTH snapshots
// (survivors) whose position or width changed beyond a negligible threshold are
// animated; brand-new cells (absent from `previous`) are skipped so CSS reveals them.
// Returns the element from `next` because the previous element reference may be stale.
export function selectFlips(
  previous: Map<string, TrackedCell>,
  next: Map<string, TrackedCell>,
): FlipDecision[] {
  const decisions: FlipDecision[] = [];
  for (const [key, before] of previous) {
    const after = next.get(key);
    if (!after) continue;
    const { translateX, scaleX } = computeFlipTransform(before, after);
    if (
      Math.abs(translateX) < FLIP_MIN_TRANSLATE_PX &&
      Math.abs(scaleX - 1) < FLIP_MIN_SCALE_DELTA
    ) {
      continue;
    }
    decisions.push({ el: after.el, translateX, scaleX });
  }
  return decisions;
}

// Lit reactive controller that runs a FLIP pass over table cells on every host
// render: it captures cell geometry before the update and, after the DOM settles,
// animates any cell whose width/position changed (e.g. a column re-flowed because a
// new row arrived). New cells have no previous geometry, so they are left to the CSS
// reveal animation. Honors prefers-reduced-motion.
export class FlipController implements ReactiveController {
  private readonly host: FlipHost;
  private readonly selector: string;
  private previous = new Map<string, TrackedCell>();

  constructor(host: FlipHost, selector = "[data-flip-key]") {
    this.host = host;
    this.selector = selector;
    host.addController(this);
  }

  hostUpdate(): void {
    this.previous = this.snapshot();
  }

  hostUpdated(): void {
    const next = this.snapshot();
    if (!prefersReducedMotion()) {
      for (const { el, translateX, scaleX } of selectFlips(
        this.previous,
        next,
      )) {
        this.play(el, translateX, scaleX);
      }
    }
    this.previous = next;
  }

  private snapshot(): Map<string, TrackedCell> {
    const cells = new Map<string, TrackedCell>();
    const nodes = this.host.renderRoot.querySelectorAll<HTMLElement>(
      this.selector,
    );
    nodes.forEach((el) => {
      const key = el.dataset.flipKey;
      if (!key) return;
      const rect = el.getBoundingClientRect();
      cells.set(key, { left: rect.left, width: rect.width, el });
    });
    return cells;
  }

  private play(el: HTMLElement, translateX: number, scaleX: number): void {
    el.animate(
      [
        {
          transformOrigin: "left center",
          transform: `translateX(${translateX}px) scaleX(${scaleX})`,
        },
        {
          transformOrigin: "left center",
          transform: "translateX(0) scaleX(1)",
        },
      ],
      { duration: FLIP_DURATION_MS, easing: FLIP_EASING },
    );
    const inner = el.querySelector<HTMLElement>(".jonas-cell");
    if (!inner) return;
    const inverse = scaleX === 0 ? 1 : 1 / scaleX;
    inner.animate(
      [
        { transformOrigin: "left center", transform: `scaleX(${inverse})` },
        { transformOrigin: "left center", transform: "scaleX(1)" },
      ],
      { duration: FLIP_DURATION_MS, easing: FLIP_EASING },
    );
  }
}
